<script>
    import cloneDeep from 'lodash/cloneDeep';
    import { generateChartData, chartDataGroupBy } from '@/utils/chart';
    import PageHeader from '@/components/common/PageHeader.vue';
    import TimeRangeCalendar from '@/components/common/TimeRangeCalendar.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import MultiselectSpaces from '@/components/common/MultiselectSpaces.vue';
    import MultiEChartCard from '@/components/common/MultiEChartCard.vue';
    import EChart from '@/components/common/EChart.vue';
    import SiteStatistics from './parts/SiteStatistics.vue';

    export default {
        name: 'ReportsPagesMixin',
        components: {
            Multiselect,
            MultiselectSpaces,
            PageHeader,
            TimeRangeCalendar,
            MultiEChartCard,
            EChart,
            SiteStatistics,
        },
        data() {
            return {
                requestCharts: this.$req('userStats/SpaceReport'),
                currentChart: null, // ustawimy w created()
                lineChartData: null, // ustawimy w created()
                pieChartData: null, // ustawimy w created()
                timeRange: null,
                pages: [],
                searchText: '',
                spaces: [],
                isSpacesLoading: false,
            };
        },
        computed: {
            userRoleShort() {
                return this.$store.getters['auth/getRoleShortName'];
            },
            impressionsMetricKey() {
                return this.userRoleShort === 'widgeter' ? 'widget_impressions' : 'offers_impressions';
            },
            metricsOrder() {
                return [this.impressionsMetricKey, 'clicks', 'orders', 'amount', 'publisher_commission'];
            },
            metricToI18nKey() {
                return {
                    [this.impressionsMetricKey]: 'widgeterReportsShops.impressions',
                    clicks: 'widgeterReportsShops.clicks',
                    orders: 'widgeterReportsShops.transactions',
                    amount: 'widgeterReportsShops.transactionAmount',
                    publisher_commission: 'widgeterReportsShops.commission',
                };
            },
            defaultLineChartData() {
                const baseObject = {};
                this.metricsOrder.forEach(metricName => {
                    baseObject[metricName] = {
                        series: [],
                        name: this.$t(this.metricToI18nKey[metricName]),
                    };
                });
                return Object.freeze(baseObject);
            },
            defaultPieChartData() {
                return Object.freeze(cloneDeep(this.defaultLineChartData));
            },
            isSpaceReportLoading() {
                return this.requestCharts.getLoading();
            },
            loading() {
                return this.isSpaceReportLoading;
            },
            chartDataGroupBy() {
                return chartDataGroupBy(this.timeRange);
            },
            chartDataFeature() {
                return this.currentChart;
            },
            spaceReport() {
                return this.requestCharts.getData();
            },
            currentChartDataExist() {
                return this.lineChartData[this.currentChart]?.series?.length && this.pieChartData[this.currentChart]?.series?.length;
            },
            spaceIds() {
                return this.pages.map(pageItem => pageItem.id);
            },
            requestParams() {
                return {
                    startDate: this.timeRange.startDate,
                    endDate: this.timeRange.endDate,
                    feature: this.chartDataFeature,
                    groupBy: this.chartDataGroupBy,
                    spaces: this.spaceIds,
                    campaigns: this.campaignId || '',
                };
            },
            apiParams() {
                return { requestParams: this.requestParams };
            },
        },
        watch: {
            timeRange(newValue, oldValue) {
                if (oldValue !== null) {
                    this.clearChartData();
                    this.fetchSpaceReport();
                }
            },
            currentChart() {
                if (!this.currentChartDataExist) {
                    this.fetchSpaceReport();
                }
            },
            pages() {
                this.clearChartData();
                this.fetchSpaceReport();
            },
            campaignId: {
                async handler() {
                    this.pages = [];
                },
            },
            spaceReport() {
                this.initChartData();
            },
        },
        methods: {
            async fetchSpaceReport() {
                return this.requestCharts.get(this.apiParams);
            },
            clearChartData() {
                this.lineChartData = cloneDeep(this.defaultLineChartData);
                this.pieChartData = cloneDeep(this.defaultPieChartData);
            },
            initChartData() {
                this.initPieChartData();
                this.initLineChartData();
            },
            initLineChartData() {
                const dataPointsByMetric = {};

                this.spaceReport?.chart?.forEach(reportRow => {
                    Object.keys(reportRow)
                        .filter(columnName => columnName !== 'date')
                        .forEach(metricName => {
                            if (!dataPointsByMetric[metricName]) dataPointsByMetric[metricName] = [];
                            dataPointsByMetric[metricName].push({ date: reportRow.date, value: reportRow[metricName] });
                        });
                });

                const seriesForECharts = Object.keys(dataPointsByMetric).map(metricName => {
                    const normalizedPoints = generateChartData(this.timeRange, dataPointsByMetric[metricName], this.chartDataGroupBy).map(point => [Number(point.date), Number(point.value) || 0]); // [x(timestamp), y(number)]
                    return { name: metricName, data: normalizedPoints };
                });

                const chartDataForCurrent = {
                    ...this.defaultLineChartData[this.currentChart],
                    series: seriesForECharts,
                    xAxis: { type: 'time' },
                };

                this.$set(this.lineChartData, this.currentChart, chartDataForCurrent);
            },
            initPieChartData() {
                const hasAnyValue = !!this.spaceReport?.pie_chart?.reduce((sum, row) => sum + (row?.[1] || 0), 0);
                const pieChartDataItems = hasAnyValue ? this.spaceReport.pie_chart.map(([label, totalValue]) => ({ name: label, value: Number(Number(totalValue).toFixed(2)) })) : [];
                const pieSeries = pieChartDataItems.length ? [{ type: 'pie', data: pieChartDataItems }] : [];
                this.$set(this.pieChartData[this.currentChart], 'series', pieSeries);
            },
            setCurrentChart(currentChart) {
                this.currentChart = currentChart;
            },
            setTimeRange(timeRange) {
                this.timeRange = timeRange;
            },
        },
        created() {
            this.currentChart = this.impressionsMetricKey;
            this.lineChartData = cloneDeep(this.defaultLineChartData);
            this.pieChartData = cloneDeep(this.defaultPieChartData);
        },
    };
</script>
